import "./UrlStatusBanner.scss";
import { Alert, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { BannerConfigItem, BannerStatus, Props } from "../../types/UrlStatusBanner.type";
import { AmazonURLStatus } from "../Enum/AmazonURLStatus";
import DownloadPdfButton from "./DownloadPdfButton";
import DownloadIcon from "@mui/icons-material/Download";
import { AMAZON_INVITATION_URL, AMAZON_URL_PROCESS_PDF } from "./constants";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AccountService from "../../Services/http/AccountService";
import { setDefaultAccount } from "../../Redux/AccountSlice";
import { SessionSorageInstance } from "../../Services/SessionStorageService";
import { UserRolesEnum } from "../../Models/user";

export default function UrlStatusBanner({ status, onDetails }: Props) {
  const { t } = useTranslation("urlStatusBanner");
  const [actionLoadingConfirm, setActionLoadingConfirm] = useState(false);
  const dispatch = useDispatch();
  const defaultAccount = useSelector((state: any) => state.account.default_account);
  const [actionError, setActionError] = useState<string | null>(null);
  const userRole = useSelector((state: any) => state.general.userRol);
  const canCompleteOnboarding  = userRole === UserRolesEnum.analyst || userRole === UserRolesEnum.admin;
  const [actionLoadingComplete, setActionLoadingComplete] = useState(false);

  const changeDefaultAccount = (amazon_authorized_url_status: AmazonURLStatus) => {
    const updatedAccount = {
      ...defaultAccount,
      amazon_authorized_url_status,
    };
    dispatch(setDefaultAccount(updatedAccount));
    SessionSorageInstance.setDefaultAccount(updatedAccount);
  };
  const confirmInvitationAccepted = async () => {
    if (!defaultAccount?.id) {
      setActionError(t("errors.noAccountSelected"));
      return;
    }
    setActionLoadingConfirm(true);
    setActionError(null);

    try {
      const res = await AccountService.confirmAmazonInvitationAccepted(defaultAccount.id);

      if (res?.success) {
        changeDefaultAccount(AmazonURLStatus.AWAITING_COMPLETION);
        return;
      }
      setActionError(t("errors.couldNotConfirmAmazon"));
    } catch (e: any) {
      setActionError(t("errors.network"));
    } finally {
      setActionLoadingConfirm(false);
    }
  };

  const completeOnboarding = async () => {
    setActionLoadingComplete(true);
    try {
      const res = await AccountService.completeAmazonUrlOnboarding(defaultAccount.id);
      if (res && res.message === 'success') {
        changeDefaultAccount(AmazonURLStatus.COMPLETED);
        return;
      } else {
        if (res && res.message) {
          setActionError(res.message);
          return;
        }
        setActionError(t("errors.couldNotComplete"));
      }
    } catch (e: any) {
      setActionError(e?.message || t("errors.network"));
    } finally {
      setActionLoadingComplete(false);
    }
  };

  const config = {
    [AmazonURLStatus.SUBMITTED]: {
      severity: "warning" as const,
      message: t("messages.submitted"),
      action: (
        <Button className="urlStatusBanner__btn" variant="outlined" color="warning" onClick={onDetails}>
          {t("buttons.viewDetails")}
        </Button>
      ),
    },
    [AmazonURLStatus.REJECTED]: {
      severity: "error" as const,
      message: t("messages.rejected"),
      action: (
        <Button className="urlStatusBanner__btn" variant="outlined" color="error" onClick={onDetails}>
          {t("buttons.viewDetails")}
        </Button>
      ),
    },
    [AmazonURLStatus.APPROVED]: {
      severity: "success" as const,
      message: t("messages.approved"),
      action: (
        <Stack direction="column" className="urlStatusBanner__actions">
          {/* Go to Amazon */}
          <Button
            className="urlStatusBanner__btn urlStatusBanner__btn--wide"
            variant="contained"
            color="success"
            href={AMAZON_INVITATION_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("buttons.goToAmazon")}
          </Button>

          {/* PDF */}
          <DownloadPdfButton
            className="urlStatusBanner__btn urlStatusBanner__btn--wide"
            href={AMAZON_URL_PROCESS_PDF}
            variant="outlined"
            color="success"
            startIcon={<DownloadIcon color="success" />}
          />

          {/* Customer completed */}
          <Button
            className="urlStatusBanner__btn urlStatusBanner__btn--wide"
            variant="outlined"
            color="success"
            onClick={confirmInvitationAccepted}
            disabled={actionLoadingConfirm}
            startIcon={actionLoadingConfirm ? <CircularProgress size={16} color="success" /> : null}
          >
            {actionLoadingConfirm ? t("buttons.working") : t("buttons.iHaveCompleted")}
          </Button>
          {actionError && (
            <Alert severity="error" variant="outlined" className="urlStatusBanner__error">
              {actionError}
            </Alert>
          )}
        </Stack>
      ),
    },
    [AmazonURLStatus.AWAITING_COMPLETION]: {
      severity: "info" as const,
      message: t("messages.awaitingCompletion"),
      action: canCompleteOnboarding  ? (
        <Stack className="urlStatusBanner__actions">
          <Button
            className="urlStatusBanner__btn"
            variant="outlined"
            color="info"
            onClick={completeOnboarding}
            disabled={actionLoadingComplete}
            startIcon={
              actionLoadingComplete ? (
                <CircularProgress size={16} color="success" />
              ) : null
            }
          >
            {actionLoadingComplete
              ? t("buttons.working")
              : t("buttons.complete")}
          </Button>

          {actionError && (
            <Alert
              severity="error"
              variant="outlined"
              className="urlStatusBanner__error"
            >
              {actionError}
            </Alert>
          )}
        </Stack>
      ) : null,
    },
  } satisfies Record<BannerStatus, BannerConfigItem>;

  const { severity, message, action } = config[status];

  return (
    <Alert severity={severity} variant="standard" className="urlStatusBanner" action={action}>
      <Typography variant="body2" className="urlStatusBanner__message">
        {message}
      </Typography>
    </Alert>
  );
}
